import React, { useState, useEffect, useCallback } from 'react';
import { FiKey, FiUsers, FiAlertCircle, FiCheck, FiShield, FiEye, FiSettings, FiZap } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import * as usersApi from '../../api/users';

interface RoleDef {
  key: string;
  label: string;
  description: string;
  permissions: string[];
  color: string;
  bgColor: string;
  icon: React.ReactNode;
}

const ROLE_DEFINITIONS: RoleDef[] = [
  {
    key: 'owner',
    label: 'Proprietaire',
    description: 'Acces complet a tous les parametres, la facturation et la gestion des membres.',
    permissions: ['read', 'write', 'delete', 'deploy', 'manage_projects', 'manage_users', 'manage_billing', 'admin'],
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    icon: <FiShield size={22} />,
  },
  {
    key: 'admin',
    label: 'Administrateur',
    description: 'Peut creer des projets, inviter des membres et gerer les architectures.',
    permissions: ['read', 'write', 'delete', 'deploy', 'manage_projects', 'manage_users'],
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: <FiSettings size={22} />,
  },
  {
    key: 'member',
    label: 'Membre',
    description: 'Peut creer et modifier des architectures dans les projets auxquels il a acces.',
    permissions: ['read', 'write', 'deploy'],
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: <FiZap size={22} />,
  },
  {
    key: 'viewer',
    label: 'Lecteur',
    description: 'Acces en lecture seule. Peut consulter les projets et architectures.',
    permissions: ['read'],
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: <FiEye size={22} />,
  },
];

const PERMISSION_LABELS: Record<string, { label: string; desc: string }> = {
  read:             { label: 'Lecture',              desc: 'Consulter les ressources' },
  write:            { label: 'Écriture',             desc: 'Créer et modifier' },
  delete:           { label: 'Suppression',          desc: 'Supprimer des ressources' },
  deploy:           { label: 'Déploiement',          desc: 'Lancer des exécutions Terraform' },
  manage_projects:  { label: 'Gérer projets',        desc: 'Créer et archiver des projets' },
  manage_users:     { label: 'Gérer utilisateurs',   desc: 'Inviter et supprimer des membres' },
  manage_billing:   { label: 'Facturation',          desc: 'Gérer abonnement et paiements' },
  admin:            { label: 'Administration',       desc: 'Accès complet sans restriction' },
};

const ALL_PERMISSION_KEYS = ['read', 'write', 'delete', 'deploy', 'manage_projects', 'manage_users', 'manage_billing', 'admin'];

const PermissionsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const loadCounts = useCallback(async () => {
    try {
      const users = await usersApi.listUsers();
      const counts: Record<string, number> = {};
      for (const u of users) {
        counts[u.role] = (counts[u.role] || 0) + 1;
      }
      setUserCounts(counts);
    } catch {
      // Non bloquant
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('permissions.header.title')}</h2>
        <p className="text-gray-600 mt-1">{t('permissions.header.subtitle')}</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex items-start gap-3">
        <FiAlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Les roles sont assignes par utilisateur depuis la page <strong>Utilisateurs</strong>.
          Chaque role dispose d'un ensemble fixe de permissions defini ci-dessous.
        </p>
      </div>

      <div className="space-y-6">
        {ROLE_DEFINITIONS.map((role) => {
          const count = userCounts[role.key] || 0;
          return (
            <div key={role.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full ${role.bgColor} ${role.color} flex items-center justify-center flex-shrink-0`}>
                      {role.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-gray-900">{role.label}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${role.bgColor} ${role.color}`}>
                          {role.key}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{role.description}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-2 text-gray-600">
                      <FiUsers size={16} />
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="font-semibold text-gray-800">{count}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">utilisateur{count !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Permissions</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {ALL_PERMISSION_KEYS.map((permKey) => {
                      const hasPermission = role.permissions.includes(permKey);
                      const perm = PERMISSION_LABELS[permKey];
                      return (
                        <div key={permKey} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${hasPermission ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                          {hasPermission ? (
                            <FiCheck size={14} className="flex-shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 block" />
                            </span>
                          )}
                          <span className={`text-xs font-medium ${!hasPermission ? 'line-through opacity-50' : ''}`}>
                            {perm?.label || permKey}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <FiKey size={20} className="text-gray-600" />
          <h3 className="font-semibold text-gray-800">Lexique des permissions</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ALL_PERMISSION_KEYS.map((key) => {
            const perm = PERMISSION_LABELS[key];
            return (
              <div key={key} className="flex items-start gap-3">
                <span className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700 flex-shrink-0">{key}</span>
                <p className="text-sm text-gray-600">{perm?.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
